// @flow
import React, {useContext, useEffect, useState} from "react"
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import ArchiverClientApi from "../API/archiverClientApi";
import {useLocation, useParams} from "react-router-dom";
import SamplesList from "./SamplesList";
import Header from "./Header";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import {useHistory} from "react-router";
import {clearWatchList} from "../ACTIONS/clientActions";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import {useCookies} from "react-cookie";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import LICache from "../SUPPORT/LICache";
import TextInput from "../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../GUI_COMMON/COMPONENTS/LIButton";

const CaseInventoryPage = () => {

    const [globalState,dispatch] = useContext(StoreContext);
    const [isLoadingInventory,setIsLoadingInventory] = useState({numLoading:0});
    const [sampleIsSelected, setSampleIsSelected] = useState(false);
    const [includeDeleted, setIncludeDeleted] = useState(true);
    const [sampleInventories,setSampleInventories] = useState({});
    const [filterText, setFilterText] = useState(null);

    const { caseId, caseNum } = useParams();
    
    const location = useLocation();
    const history = useHistory();
    const [cookies] = useCookies();
    const textFilterRef = React.createRef();
    
    const [comingFromRetrievalOrder] = useState(location.state?.roInProgress);
    const [comingFromRetrievalOrderId] = useState(location.state?.roOrderId); // will be undefined/null for NO
    
    useEffect(() => {
        // RECALL -- this runs AFTER all children mount!
        console.debug("ShowCaseInventoryPage starting up...");
        window.scrollTo(0,0);
        LIUtils.setBackgroundColor('--li-main-bg-color');

        if(globalState.sampleTypes.initialized && globalState.sampleTypes.items.length>0) {
            globalState.sampleTypes.items.forEach( (type) => {
                getSampleInventoryFromServer(caseId,type.id,includeDeleted);
            });
        }
        
        if(!comingFromRetrievalOrder) { // clear out the current order, to build a new one
            (async () => { await LICache.clearRetrievalInfo()})();  // IIFE
        }
    }, [caseId,globalState.sampleTypes,includeDeleted]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    function getSampleInventoryFromServer(caseId,sampleType,includeDeleted) {
        setIsLoadingInventory(prevState => {
            prevState[sampleType]=1;
            prevState.numLoading=(prevState?.numLoading??0)+1;
            return {...prevState};
        });
        ArchiverClientApi.getCaseSampleInventory(caseId,includeDeleted,sampleType)
        .then( (samples) => {
            const sortedSamples = samples.sort((a,b) => a.sampleNumber?.localeCompare(b?.sampleNumber??""));
            setSampleInventories((prev) => {
                return {...prev,[sampleType]:sortedSamples};
            });
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoadingInventory(prevState => {
                delete prevState[sampleType];
                prevState.numLoading=prevState.numLoading-1;
                return {...prevState};
            });
        });
    }
    
    function reportNumFound(numFound) {
        return (
            <div className="text-left text-muted">{numFound} total</div>
        );
    }
    
    function handleSampleClicked(sampleId) {
        history.push(`/sample_details/${sampleId}`); 
    }
    
    function handleSampleRepairClicked(sampleId) {
        history.push({
            pathname: `/attention_edit/${sampleId}`,
            state: {from:"case_inventory"}
        })
    }
    
    function handleSampleOrderClicked(orderId) {
        history.push(`/retrievals/details/${orderId}`);
    }
    
    function handleCheckAll(sampleType:number){
        
        const sampleInventory = sampleInventories[sampleType];
        
        function setCBAndParent(cbItem,inventoryList, isChecked) {
            cbItem.checked=isChecked;
            const parentBlock = inventoryList.find( sample => sample.id===Number(cbItem.dataset.parentId));
            parentBlock.checked=isChecked;  // set the parent too
        }
        
        const isChecked = document.getElementById("CB_"+sampleType).checked;
        const blockCBs = document.getElementsByName(sampleType+"-cb");
        blockCBs.forEach(item => {
                if (item.disabled) { // can't check samples that are out
                    setCBAndParent(item,sampleInventory,false);
                }
                else {
                    setCBAndParent(item,sampleInventory,isChecked);
                }
            }
        );
        updateIsSomethingChecked();
    }
    
    function handleSampleCheckChanged(item,isChecked) {
        item.checked=isChecked; // augment
        updateIsSomethingChecked();
    }
    
    function updateIsSomethingChecked() {
        let isSomethingChecked = globalState.sampleTypes.items.find( (type) => {
            const haveCheckedItem = sampleInventories[type.id].find( item => item.checked===true);
            return haveCheckedItem!==undefined;
        });
        
        setSampleIsSelected(isSomethingChecked!==undefined);
    }

    /**
     * async!
     * @returns {Promise<void>}
     */
    async function handleMarkForRetrieval() {
        if(!LIUtils.UserHasLevel(cookies,"RETRIEVER")) {
            Popup.show("", "<p>You are not a RETIEVER type user.</p>" +
                "Talk to your Archiver Administrator if you wish to change this.");
            return;
        }
        
        for(const type of globalState.sampleTypes.items) {
            const samples = sampleInventories[type.id];
            if(samples && samples.length>0) {
                const checkedSamples = samples.filter(item => item.checked===true);
                await LICache.addSamplesToRetrievalList(checkedSamples);
            }
        }

        redirectToRetrievalOrders();
    }
    
    function handleNotifyWhenAvailable() {

        const checkSamples = [];
        globalState.sampleTypes.items.forEach( (type) => {
            const samples = sampleInventories[type.id];
            if(samples) {
                checkSamples.push(samples.filter(item => item.checked===true)??[]);
            }
        });
        
        const idsOnly = checkSamples.map( item => item.id);
        ArchiverClientApi.sendAddSamplesToWatchList(idsOnly)
        .then( (totalAdded) => {
            dispatch(clearWatchList());  // ensure it's loaded again when user wants to see it.
            Popup.show("",`Added ${totalAdded} samples to your watch list`);
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError));
    }
    
    function handleBackClick() {
        if(comingFromRetrievalOrder) { // need to ensure we reload and not clear the previous form
            redirectToRetrievalOrders();
        }
        else {
            history.goBack();
        }
    }
    
    function showMarkForNotifyButton() {
        if(!comingFromRetrievalOrder) {
            return <button className="li-button li-primary li-font-medium h-100 p-2" disabled={!sampleIsSelected}
                           onClick={handleNotifyWhenAvailable}>Notify When Available</button>;
        }
    }

    function handleShowDeletedCBChanged(e) {
        e.stopPropagation();
        setIncludeDeleted(e.target.checked);
    }
    
    function dumpSampleInventories() {
        
        let out = globalState.sampleTypes?.items.map( (type) => {
            return dumpSampleTypeInventory(type.name,type.id, sampleInventories[type.id]); 
        });
        
        const count = out.reduce((acc, item) => {if(item!==null) acc=1; return acc;},0);
        if(count===0) {
            return (<div className="mb-5">
                {(isLoadingInventory?.numLoading??1)>0 && <LoadingSpinner center medium/>}
                {(isLoadingInventory?.numLoading??1)===0 && <p>No samples found</p>}
                </div>);
        }
        
        return (
            <div id="caseInventoryList">
                {out}
            </div>
        );
    }

    function dumpSampleTypeInventory(headerText,sampleType,inventory:[]) {
        if(inventory===undefined || !inventory || inventory.length===0) { // skip
            return null;
        }
        
        let filteredInventory = inventory;
        if(filterText) { // filter is employed
            filteredInventory = inventory?.filter(item =>{
                if (item?.sampleNumber && item.sampleNumber?.toString().toLowerCase().indexOf(filterText.toLowerCase()) !== -1) {
                    return true;
                }
                return false;
            });
        }
        
        return(
            <div key={sampleType} id={"inventory-"+sampleType} className="card li-bg-blue-card mb-4 p-1">
                <div className="card-header row m-0 p-0 justify-content-between"> 
                    <div>
                        <h3 className="d-inline-block">{headerText}</h3>
                        {filteredInventory.length>1 && <span className="align-self-center">
                            <input type="checkbox" id={"CB_"+sampleType} className="ml-4 mr-2" aria-label="Checkbox" 
                                   onChange={() => handleCheckAll(sampleType)}/><i>select all</i>
                        </span>}
                    </div>
                    {reportNumFound(filteredInventory.length)}
                </div>
                <div className="li-scrollable-at-250" id={"inventory-list-"+sampleType}>
                    <SamplesList name={sampleType+"-cb"} samples={filteredInventory} sampleType={sampleType} showCheckBox onSampleCheckChanged={handleSampleCheckChanged}
                                 onSampleClicked={handleSampleClicked} onRepairClicked={handleSampleRepairClicked} onOrderClicked={handleSampleOrderClicked}
                                 allowCheckIfOut={false} allowCheckIfOffPremises={false}
                                 isLoading={false} showImages
                                 profiles={globalState.profiles?.items??[]}/>
                </div>
            </div>
        );
    }
    
    function dumpShowDeletedCB() {
        if(comingFromRetrievalOrder) return null; // don't allow selection of these in ROs
        return (
            <div className="text-right ml-auto mr-0">
                <input type="checkbox" className="m-2 medium align-self-center" name="include-deleted-cb" aria-label="Checkbox"
                       checked={includeDeleted} onChange={(e)=>handleShowDeletedCBChanged(e)}/>
                <label className="li-form-label" htmlFor="include-deleted-cb">show deleted</label>
            </div>
        );
    }

    //#region DATA FILTER
    function handleDataFilterChanged() {
        if(textFilterRef.current) {
            const filterText = textFilterRef.current.value;
            if(filterText==="") {
                setFilterText(null);
            }
            else {
                setFilterText(filterText);
            }
        }
    }

    function handleClearFilterClicked() {
        if(textFilterRef.current) {
            textFilterRef.current.value = "";
        }
        setFilterText(null);
    }

    function dumpSampleFilter() {
        return (
            <div className="row m-0 p-0 align-self-center">
                <TextInput refName={textFilterRef} name="textFilter" label="Sample # Filter" oneRow textLeft
                           maxLength={20} onChange={handleDataFilterChanged}/>
                <div className="align-self-center">
                    <LIButton bgClass="ml-1 mt-3" faIcon="fa fa-times-circle" small onClick={handleClearFilterClicked}/>
                </div>
            </div>
        );
    }
    //#endregion DATA FILTER
    
    function dumpFilterRow() {
        return(
            <>
                {dumpSampleFilter()} 
                {dumpShowDeletedCB()}
            </>
        );
        
    }
    
    function redirectToRetrievalOrders() {
        if (comingFromRetrievalOrder) { // redirect to the order we were working on
            history.push({
                pathname: `/retrievals/details/${comingFromRetrievalOrderId}`,
                state: {roInProgress: true, roOrderId: comingFromRetrievalOrderId}  // take from localstorage
            });
            return;
        }

        // new order!
        history.push({
            pathname: `/retrievals/new`, state: {roInProgress: true, roOrderId: -1}  // take from localstorage?
        });
    }

    return (
        <div className="container">
            <Header/>
            <div className="row li-header-row justify-content-between">
                <div>
                    <BackButton onClick={handleBackClick}/>
                    <h1 className="d-inline-block align-middle">Case: 
                        <span className="li-fg-primary ml-2">{caseNum}</span>
                    </h1>
                </div>
                <div className="li-font-small text-right my-auto ml-auto mr-3">[id: {caseId}]</div>
            </div>
            <div className="row justify-content-center mb-4">
                {(isLoadingInventory?.numLoading??0)>0 && <div className="d-inline-block align-self-center mr-5"><LoadingSpinner medium/></div>}
                {(isLoadingInventory?.numLoading??0)===0 && dumpFilterRow()}
            </div>

            {dumpSampleInventories()}
            
            <div className="row justify-content-center">
                <button id="markForRemovalBtn" className="li-button li-primary li-font-medium h-100 p-2 mr-5" disabled={!sampleIsSelected} 
                        onClick={handleMarkForRetrieval}>Mark For Retrieval</button>
                {showMarkForNotifyButton()}
            </div>
        </div>
    );
}

export default CaseInventoryPage;